/**
 * Categorical color palette for the Avatar block, drawn from the
 * `--color-chart-*` tokens in `tailwind.css`.
 *
 * The Avatar takes a 1-based palette index (`color`) and renders the matching
 * block. How that index is chosen is the caller's concern:
 *
 *  - For projects, derive the index from the project's stable position in the
 *    team's ordered list (`getProjectColorIndex` in `lib/project-color`) so the
 *    Avatar block, the by-project chart series, and any other per-project
 *    color stay in sync without collisions for the first N projects.
 *  - For arbitrary strings where a permanent-per-id color is the goal and
 *    occasional collisions are acceptable, `getColorIndexFromSeed` hashes a
 *    seed to a palette index.
 */

export const AVATAR_PALETTE_SIZE = 16;

// Literal classes so Tailwind's scanner keeps them.
export const AVATAR_COLOR_CLASSES = [
  'bg-chart-1',
  'bg-chart-2',
  'bg-chart-3',
  'bg-chart-4',
  'bg-chart-5',
  'bg-chart-6',
  'bg-chart-7',
  'bg-chart-8',
  'bg-chart-9',
  'bg-chart-10',
  'bg-chart-11',
  'bg-chart-12',
  'bg-chart-13',
  'bg-chart-14',
  'bg-chart-15',
  'bg-chart-16',
] as const;

// Normalize a 1-based index to a 0..N-1 array offset, wrapping past the
// palette size (and handling 0 / negative inputs gracefully).
const normalizeIndex = (colorIndex: number): number => {
  const size = AVATAR_PALETTE_SIZE;
  return (((Math.trunc(colorIndex) - 1) % size) + size) % size;
};

/** Tailwind background class for a palette index (1-based; wraps). */
export const getAvatarColorClass = (colorIndex: number): string =>
  AVATAR_COLOR_CLASSES[normalizeIndex(colorIndex)];

/**
 * CSS color value for a palette index (1-based; wraps). Use this where a raw
 * color string is needed instead of a class, e.g. a recharts series `fill`,
 * so chart series match the Avatar blocks.
 */
export const getAvatarColor = (colorIndex: number): string =>
  `var(--color-chart-${normalizeIndex(colorIndex) + 1})`;

const hashString = (value: string): number => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
};

/**
 * Derive a 1-based palette index from a seed string. The same seed always
 * maps to the same index, so this is useful when a permanent-per-id color is
 * wanted. With a fixed palette, different seeds can hash to the same index
 * (birthday paradox: with N=8, even 3 strings have a ~34% chance of
 * collision); prefer an index-by-position scheme when distinct colors among a
 * known set are required.
 */
export const getColorIndexFromSeed = (seed: string): number =>
  (hashString(seed) % AVATAR_PALETTE_SIZE) + 1;
